import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, AlertCircle, Clock, XCircle } from 'lucide-react';

/**
 * AcceptInvitation page
 * Handles the /accept-invite/:token route for accepting festival collaboration invitations
 */
const AcceptInvitation = () => {
  const { token } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, needs_verification, email_mismatch, expired, error, accepting
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch invitation info on mount
  useEffect(() => {
    const acceptInvitation = async () => {
      try {
        const response = await fetch(`/api/invitations/${token}`, {
          credentials: 'include'
        });

        if (response.redirected) {
          // The server redirected us to the festival editor
          window.location.href = response.url;
          return;
        }

        const data = await response.json();

        if (!response.ok) {
          if (data.requiresVerification) {
            setStatus('needs_verification');
          } else if (data.expectedEmail) {
            setStatus('email_mismatch');
          } else {
            setStatus('error');
            setErrorMessage(data.error || 'Failed to accept invitation');
          }
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage('Failed to accept invitation');
      }
    };

    const fetchInvitationInfo = async () => {
      try {
        const response = await fetch(`/api/invitations/${token}/info`);
        const data = await response.json();

        if (!response.ok) {
          setStatus('error');
          setErrorMessage(data.error || 'Invitation not found');
          return;
        }

        setInvitationInfo(data.invitation);

        if (data.invitation.status === 'expired' || data.invitation.isExpired) {
          setStatus('expired');
          return;
        }

        if (data.invitation.status !== 'pending') {
          setStatus('error');
          setErrorMessage(`This invitation has already been ${data.invitation.status}`);
          return;
        }

        // Check if user is authenticated
        if (!isAuthenticated) {
          setStatus('needs_login');
          return;
        }

        // Check if user's email matches
        if (user.email.toLowerCase() !== data.invitation.email.toLowerCase()) {
          setStatus('email_mismatch');
          return;
        }

        // Check if user needs to verify their email
        const roles = user.roles || [];
        if (roles.includes('unverified')) {
          setStatus('needs_verification');
          return;
        }

        // All checks passed - accept the invitation
        setStatus('accepting');
        acceptInvitation();
      } catch (err) {
        setStatus('error');
        setErrorMessage('Failed to load invitation details');
      }
    };

    fetchInvitationInfo();
  }, [token, isAuthenticated, user]);

  // Common container style
  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center'
  };

  const iconContainerStyle = {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  };

  const buttonStyle = {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '14px',
    marginTop: '20px'
  };

  // Loading state
  if (status === 'loading' || status === 'accepting') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: '#e3f2fd' }}>
            <Clock size={32} style={{ color: '#2196f3' }} />
          </div>
          <h2 style={{ marginBottom: '12px', color: '#333' }}>
            {status === 'loading' ? 'Loading invitation...' : 'Accepting invitation...'}
          </h2>
          <p style={{ color: '#666' }}>Please wait while we process your request.</p>
        </div>
      </div>
    );
  }

  // Needs login state
  if (status === 'needs_login') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: '#fff3e0' }}>
            <Mail size={32} style={{ color: '#ff9800' }} />
          </div>
          <h2 style={{ marginBottom: '12px', color: '#333' }}>Collaboration Invitation</h2>
          {invitationInfo && (
            <>
              <p style={{ color: '#666', marginBottom: '8px' }}>
                <strong>{invitationInfo.inviterName}</strong> has invited you to collaborate on
              </p>
              <p style={{ fontSize: '18px', fontWeight: 500, color: '#333', marginBottom: '16px' }}>
                "{invitationInfo.festivalName}"
              </p>
            </>
          )}
          <p style={{ color: '#666' }}>Please log in to accept this invitation.</p>
          <Link
            to={`/user?redirect=/accept-invite/${token}`}
            style={buttonStyle}
          >
            Log In to Accept
          </Link>
        </div>
      </div>
    );
  }

  // Needs email verification
  if (status === 'needs_verification') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: '#fff3e0' }}>
            <AlertCircle size={32} style={{ color: '#ff9800' }} />
          </div>
          <h2 style={{ marginBottom: '12px', color: '#333' }}>Email Verification Required</h2>
          {invitationInfo && (
            <p style={{ color: '#666', marginBottom: '16px' }}>
              To collaborate on <strong>"{invitationInfo.festivalName}"</strong>, you need to verify your email address first.
            </p>
          )}
          <p style={{ color: '#666' }}>
            Please go to your Account page to resend the verification email.
          </p>
          <Link to="/user/account" style={buttonStyle}>
            Go to Account Settings
          </Link>
          <p style={{ color: '#999', fontSize: '12px', marginTop: '16px' }}>
            After verifying your email, return to this page or click the invitation link again.
          </p>
        </div>
      </div>
    );
  }

  // Email mismatch
  if (status === 'email_mismatch') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: '#ffebee' }}>
            <XCircle size={32} style={{ color: '#f44336' }} />
          </div>
          <h2 style={{ marginBottom: '12px', color: '#333' }}>Wrong Account</h2>
          {invitationInfo && (
            <>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                This invitation was sent to <strong>{invitationInfo.email}</strong>, but you're logged in as <strong>{user?.email}</strong>.
              </p>
              <p style={{ color: '#666' }}>
                Please log out and sign in with the correct account to accept this invitation.
              </p>
            </>
          )}
          <Link to="/user/account" style={buttonStyle}>
            Go to Account Settings
          </Link>
        </div>
      </div>
    );
  }

  // Expired
  if (status === 'expired') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: '#ffebee' }}>
            <Clock size={32} style={{ color: '#f44336' }} />
          </div>
          <h2 style={{ marginBottom: '12px', color: '#333' }}>Invitation Expired</h2>
          <p style={{ color: '#666' }}>
            This invitation has expired. Please ask the festival owner to send a new invitation.
          </p>
          <Link to="/user" style={buttonStyle}>
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ ...iconContainerStyle, backgroundColor: '#ffebee' }}>
          <XCircle size={32} style={{ color: '#f44336' }} />
        </div>
        <h2 style={{ marginBottom: '12px', color: '#333' }}>Something Went Wrong</h2>
        <p style={{ color: '#666' }}>{errorMessage || 'An unexpected error occurred.'}</p>
        <Link to="/user" style={buttonStyle}>
          Go to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default AcceptInvitation;